/**
 * Converts a key to a more readable format for table headers
 * @param {string} key - the key from an object to be converted
 * @returns {string} - the converted key
 * @description This function takes a key and converts it to a more readable format for table headers.
 * It adds spaces between camel case letters, adds spaces between numbers and letters, capitalizes the first letter, and handles acronyms.
 * For example, "firstName" becomes "First Name", "lastName" becomes "Last Name", and "IDNumber" becomes "ID Number".
 * It also takes into account acronyms like "ID" and "USA" by adding spaces between them and the following camel case letters.
 * For example, "IDNumber" becomes "ID Number", "ID1" becomes "ID 1", and "USACitizen" becomes "USA Citizen".
 * It also adds spaces between lowercase letters and numbers, so "test1" becomes "Test 1".
 */
const formatKeyToTitle = (key) => {
  if (typeof key !== 'string') return '';

  // Split on transitions: lower->upper, acronym->camel, letter->number, number->letter
  // This avoids ambiguous or nested quantifiers.
  const parts = [];
  let lastIdx = 0;

  for (let i = 1; i < key.length; i++) {
    const prev = key[i - 1];
    const curr = key[i];

    // lower->upper (camelCase)
    if (/[a-z]/.test(prev) && /[A-Z]/.test(curr)) {
      parts.push(key.slice(lastIdx, i));
      lastIdx = i;
    }
    // acronym->camel (IDNumber)
    else if (
      /[A-Z]/.test(prev) &&
      /[A-Z]/.test(curr) &&
      i + 1 < key.length &&
      /[a-z]/.test(key[i + 1])
    ) {
      parts.push(key.slice(lastIdx, i));
      lastIdx = i;
    }
    // letter->number or number->letter
    else if ((/[a-zA-Z]/.test(prev) && /\d/.test(curr)) || (/\d/.test(prev) && /[a-zA-Z]/.test(curr))) {
      parts.push(key.slice(lastIdx, i));
      lastIdx = i;
    }
  }
  parts.push(key.slice(lastIdx));

  // Capitalize first letter and join with spaces
  const result = parts.join(' ').replace(/^./, (str) => str.toUpperCase());
  return result;
};

/**
 * Converts object keys to a more readable format for table headers
 * @param {Array} data - array of objects to be converted
 * @returns {Array} - array of objects with formatted keys
 * @description This function takes an array of objects and converts the keys of each object to a more readable format for table headers.
 * It uses the formatKeyToTitle function to convert the keys.
 */
const normalizeDataForTable = (data) => {
  if (!data || data.length === 0) {
    return [];
  }
  const keys = Object.keys(data[0]);
  return keys.map((key) => {
    const column = {
      field: key,
      title: formatKeyToTitle(key),
      width: 'auto',
    };
    return column;
  });
}

/**
 * Returns an array of selected items from table data based on selection state.
 * @param {Array} data - The table data array.
 * @param {Object} select - The selection state object (keyed by dataItemKey).
 * @param {string} dataItemKey - The unique key for each data item.
 * @returns {Array} - Array of selected items.
 */
const getSelectedArray = (data, select, dataItemKey) => {
  if (!Array.isArray(data) || !select || !dataItemKey) return [];
  return data.filter(item => select[item[dataItemKey]]);
};

export {
  formatKeyToTitle,
  normalizeDataForTable,
  getSelectedArray
}